// CommunityAppCardModal - Admin detail modal for community browse
// Shows app details, review state/controls, and Settings/Playspace/Workspace actions.

import React, { useCallback, useEffect, useState } from 'react';
import { RevoltModal, RevoltButton, DropdownSelect, RevoltTextarea } from '../../../../../components';
import { navigationManager } from '../../../../../managers/navigation-manager';
import './CommunityAppCardModal.css';

const REVIEW_MODE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'force_review', label: 'Force review' },
  { value: 'skip_review', label: 'Skip review' },
];

const REVIEW_STATUS_EDIT_OPTIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'deferred', label: 'Deferred' },
];

function formatDate(val) {
  if (!val) return '—';
  const d = val instanceof Date ? val : new Date(val);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function formatReviewMode(mode) {
  switch (mode) {
    case 'force_review':
      return 'Force review';
    case 'skip_review':
      return 'Skip review';
    case 'default':
    default:
      return 'Default';
  }
}

function formatReviewStatus(status) {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'deferred':
      return 'Deferred';
    case 'pending':
      return 'Pending';
    case 'not_required':
      return 'Not required';
    case 'private':
      return 'Private';
    default:
      return status || '—';
  }
}

function formatBypassSource(source) {
  switch (source) {
    case 'app_force_review':
      return 'App override forces review';
    case 'app_skip_review':
      return 'App override skips review';
    case 'user_force_review':
      return 'User override forces review';
    case 'user_skip_review':
      return 'User override skips review';
    case 'paid_default':
      return 'Paid/supporter default';
    case 'free_default':
      return 'Free user default';
    default:
      return '—';
  }
}

function getOwnerAccessLabel(owner) {
  if (!owner) return '—';
  if (owner.role === 'admin') return 'Admin';
  if (owner.isSupporter === true) {
    const planId = owner.membership?.planId;
    const tierId = owner.membership?.tierId;
    if (planId && tierId) return `${planId} / ${tierId}`;
    if (planId) return planId;
    return 'Membership';
  }
  return 'Free';
}

function getUserNeedsReview(owner, effectiveReview) {
  const ownerIsSupporter = effectiveReview?.ownerIsSupporter === true;
  const userReviewMode = owner?.communityPublishing?.reviewMode || 'default';
  if (userReviewMode === 'force_review') return true;
  if (userReviewMode === 'skip_review') return false;
  return !ownerIsSupporter;
}

export function CommunityAppCardModal({ app, isOpen, onClose, manager, onReviewSettingsUpdated }) {
  const [detail, setDetail] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSavingUserMode, setIsSavingUserMode] = useState(false);
  const [isSavingAppMode, setIsSavingAppMode] = useState(false);
  const [isSavingReview, setIsSavingReview] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [statusEdit, setStatusEdit] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const appId = app?.id;

  const loadDetails = useCallback(async () => {
    if (!appId || !manager?.fetchAdminAppDetails) return;
    setIsLoadingDetails(true);
    setErrorMessage('');
    try {
      const nextDetail = await manager.fetchAdminAppDetails(appId);
      setDetail(nextDetail);
      const community = nextDetail?.app?.community;
      setReviewNotes(community?.reviewNotes || '');
      const status = community?.reviewStatus;
      if (['approved', 'rejected', 'deferred'].includes(status)) {
        setStatusEdit(status);
      } else {
        setStatusEdit(null);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load admin app details');
    } finally {
      setIsLoadingDetails(false);
    }
  }, [appId, manager]);

  useEffect(() => {
    if (isOpen && appId) {
      loadDetails();
    }
    if (!isOpen) {
      setDetail(null);
      setReviewNotes('');
      setStatusEdit(null);
      setErrorMessage('');
    }
  }, [isOpen, appId, loadDetails]);

  if (!app) return null;

  const detailApp = detail?.app || app;
  const owner = detail?.owner || null;
  const effectiveReview = detail?.effectiveReview || null;
  const currentReview = detail?.currentReview || null;

  const name = detailApp.name || 'Untitled';
  const description = detailApp.description || '';
  const ownerUsername = owner?.username || detailApp.ownerUsername || '';
  const ownerId = owner?.id || detailApp.ownerId || null;
  const views = detailApp.views ?? 0;
  const likes = detailApp.likes ?? 0;
  const trendingScore = detailApp.trendingScore;
  const tags = Array.isArray(detailApp.tags) ? detailApp.tags : [];
  const isPublic = detailApp.isPublic === true;
  const template = detailApp.template || '';
  const createdAt = detailApp.createdAt;
  const hasEverSyncedFiles = detailApp.hasEverSyncedFiles === true;
  const hasNonOwnerSyncedFiles = detailApp.hasNonOwnerSyncedFiles === true;
  const userReviewMode = owner?.communityPublishing?.reviewMode || effectiveReview?.userReviewMode || 'default';
  const appReviewMode = detailApp.community?.reviewMode || effectiveReview?.appReviewMode || 'default';
  const appReviewRequired = detailApp.community?.reviewRequired === true || effectiveReview?.requiresReview === true;
  const reviewStatus = detailApp.community?.reviewStatus || 'private';
  const reviewBypassSource = detailApp.community?.reviewBypassSource || effectiveReview?.reviewBypassSource || null;
  const userNeedsReview = getUserNeedsReview(owner, effectiveReview);
  const isPendingReview = reviewStatus === 'pending' || currentReview?.status === 'pending';

  const handleSettings = () => {
    onClose();
    navigationManager.navigateToAppSettings(app.id);
  };

  const handlePlayspace = () => {
    onClose();
    navigationManager.navigateToPath(`/playspace/${app.id}?mode=sharing`);
  };

  const handleWorkspace = () => {
    onClose();
    navigationManager.navigateToPath(`/workspace/${app.id}`);
  };

  const handleUserReviewModeChange = async (e) => {
    const nextMode = e.target.value;
    if (!owner?.id || !manager?.updateUserReviewMode) return;
    setIsSavingUserMode(true);
    setErrorMessage('');
    try {
      await manager.updateUserReviewMode(owner.id, nextMode);
      await loadDetails();
      await onReviewSettingsUpdated?.();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update user review mode');
    } finally {
      setIsSavingUserMode(false);
    }
  };

  const handleAppReviewModeChange = async (e) => {
    const nextMode = e.target.value;
    if (!appId || !manager?.updateAppReviewMode) return;
    setIsSavingAppMode(true);
    setErrorMessage('');
    try {
      await manager.updateAppReviewMode(appId, nextMode);
      await loadDetails();
      await onReviewSettingsUpdated?.();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update app review mode');
    } finally {
      setIsSavingAppMode(false);
    }
  };

  const handleReviewDecision = async (action) => {
    const reviewId = currentReview?.id;
    if (!reviewId || !manager?.approveReview) return;
    setIsSavingReview(true);
    setErrorMessage('');
    try {
      await manager.approveReview(reviewId, action, reviewNotes?.trim() || null);
      setReviewNotes('');
      await loadDetails();
      await onReviewSettingsUpdated?.();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to moderate review');
    } finally {
      setIsSavingReview(false);
    }
  };

  const handleUpdateReviewStatus = async () => {
    if (!appId || !manager?.updateAppReviewStatus || !statusEdit) return;
    setIsSavingStatus(true);
    setErrorMessage('');
    try {
      await manager.updateAppReviewStatus(appId, statusEdit, reviewNotes?.trim() || null);
      await loadDetails();
      await onReviewSettingsUpdated?.();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update review status');
    } finally {
      setIsSavingStatus(false);
    }
  };

  const canModerateReview = currentReview?.status === 'pending' && manager?.approveReview;
  const canChangeStatus = ['approved', 'rejected', 'deferred'].includes(reviewStatus)
    && manager?.updateAppReviewStatus;

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={name}
      containerClassName="community-app-card-modal"
    >
      <div className="community-app-card-modal__content">
        <div className="community-app-card-modal__main">
          <div className="community-app-card-modal__left">
            <div className="community-app-card-modal__detail">
              {description && (
                <div className="community-app-card-modal__row">
                  <span className="community-app-card-modal__label">Description</span>
                  <p className="community-app-card-modal__description">{description}</p>
                </div>
              )}
            </div>
            <div className="community-app-card-modal__meta">
          <div className="community-app-card-modal__meta-row">
            <span className="community-app-card-modal__label">Owner</span>
            <span>{ownerUsername || (ownerId ? `ID: ${ownerId}` : '—')}</span>
          </div>
          <div className="community-app-card-modal__meta-row">
            <span className="community-app-card-modal__label">Views</span>
            <span>{views}</span>
          </div>
          <div className="community-app-card-modal__meta-row">
            <span className="community-app-card-modal__label">Likes</span>
            <span>{likes}</span>
          </div>
          {trendingScore != null && (
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Trending score</span>
              <span>{Number(trendingScore).toFixed(1)}</span>
            </div>
          )}
          <div className="community-app-card-modal__meta-row">
            <span className="community-app-card-modal__label">Public</span>
            <span>{isPublic ? 'Yes' : 'No'}</span>
          </div>
          {template && (
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Template</span>
              <span>{template}</span>
            </div>
          )}
          <div className="community-app-card-modal__meta-row">
            <span className="community-app-card-modal__label">Created</span>
            <span>{formatDate(createdAt)}</span>
          </div>
          <div className="community-app-card-modal__meta-row">
            <span className="community-app-card-modal__label">Ever synced</span>
            <span>{hasEverSyncedFiles ? 'Yes' : 'No'}</span>
          </div>
          <div className="community-app-card-modal__meta-row">
            <span className="community-app-card-modal__label">Non-owner synced</span>
            <span>{hasNonOwnerSyncedFiles ? 'Yes' : 'No'}</span>
          </div>
        </div>
        {tags.length > 0 && (
          <div className="community-app-card-modal__tags">
            <span className="community-app-card-modal__label">Tags</span>
            <div className="community-app-card-modal__tag-list">
              {tags.map((tag) => (
                <span key={tag} className="community-app-card-modal__tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
          </div>
          <div className="community-app-card-modal__center">
            <div className="community-app-card-modal__review-header">
              <span className="community-app-card-modal__label">Community review</span>
              {isLoadingDetails && (
                <span className="community-app-card-modal__loading">
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Loading review data...
                </span>
              )}
            </div>

            {errorMessage && (
              <div className="community-app-card-modal__error">
                <i className="fa-solid fa-circle-exclamation" aria-hidden /> {errorMessage}
              </div>
            )}

            <div className="community-app-card-modal__review-grid">
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Owner access</span>
              <span>{getOwnerAccessLabel(owner)}</span>
            </div>
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">User needs review</span>
              <span>{userNeedsReview ? 'Yes' : 'No'}</span>
            </div>
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">App needs review</span>
              <span>{appReviewRequired ? 'Yes' : 'No'}</span>
            </div>
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Review status</span>
              <span>{formatReviewStatus(reviewStatus)}</span>
            </div>
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Currently in queue</span>
              <span>{isPendingReview ? 'Yes' : 'No'}</span>
            </div>
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Decision source</span>
              <span>{formatBypassSource(reviewBypassSource)}</span>
            </div>
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Current user override</span>
              <span>{formatReviewMode(userReviewMode)}</span>
            </div>
            <div className="community-app-card-modal__meta-row">
              <span className="community-app-card-modal__label">Current app override</span>
              <span>{formatReviewMode(appReviewMode)}</span>
            </div>
            </div>
          </div>
          <div className="community-app-card-modal__right">
          {canModerateReview && (
            <div className="community-app-card-modal__review-decision">
              <span className="community-app-card-modal__label">Review decision</span>
              <div className="community-app-card-modal__review-decision-message">
                <label htmlFor={`community-review-notes-${appId}`} className="community-app-card-modal__label">
                  Message to app owner (optional)
                </label>
                <RevoltTextarea
                  id={`community-review-notes-${appId}`}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="e.g. Please sync your files before re-submitting, or add a description."
                  disabled={isSavingReview || isLoadingDetails}
                  rows={2}
                  minRows={2}
                  maxRows={4}
                  className="community-app-card-modal__review-notes-input"
                />
              </div>
              <div className="community-app-card-modal__review-decision-buttons">
                <RevoltButton
                  variant="primary"
                  size="sm"
                  onClick={() => handleReviewDecision('approve')}
                  disabled={isSavingReview || isLoadingDetails}
                  aria-label="Approve review"
                >
                  {isSavingReview ? (
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  ) : (
                    <i className="fa-solid fa-check" aria-hidden />
                  )}{' '}
                  Approve
                </RevoltButton>
                <RevoltButton
                  variant="danger"
                  size="sm"
                  onClick={() => handleReviewDecision('reject')}
                  disabled={isSavingReview || isLoadingDetails}
                  aria-label="Reject review"
                >
                  {isSavingReview ? (
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  ) : (
                    <i className="fa-solid fa-times" aria-hidden />
                  )}{' '}
                  Reject
                </RevoltButton>
                <RevoltButton
                  variant="outline"
                  size="sm"
                  onClick={() => handleReviewDecision('defer')}
                  disabled={isSavingReview || isLoadingDetails}
                  aria-label="Defer review"
                >
                  {isSavingReview ? (
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  ) : (
                    <i className="fa-solid fa-clock" aria-hidden />
                  )}{' '}
                  Defer
                </RevoltButton>
              </div>
            </div>
          )}

          {canChangeStatus && (
            <div className="community-app-card-modal__review-decision community-app-card-modal__change-status">
              <span className="community-app-card-modal__label">Change review status</span>
              <div className="community-app-card-modal__review-decision-message">
                <label htmlFor={`community-status-edit-${appId}`} className="community-app-card-modal__label">
                  Status
                </label>
                <DropdownSelect
                  id={`community-status-edit-${appId}`}
                  value={statusEdit || reviewStatus}
                  options={REVIEW_STATUS_EDIT_OPTIONS}
                  onChange={(e) => setStatusEdit(e.target.value)}
                  className="community-app-card-modal__select"
                  menuClassName="app-management-sort-menu"
                  disabled={isSavingStatus || isLoadingDetails}
                />
              </div>
              <div className="community-app-card-modal__review-decision-message">
                <label htmlFor={`community-status-notes-${appId}`} className="community-app-card-modal__label">
                  Message to app owner (optional)
                </label>
                <RevoltTextarea
                  id={`community-status-notes-${appId}`}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="e.g. Please sync your files before re-submitting."
                  disabled={isSavingStatus || isLoadingDetails}
                  rows={2}
                  minRows={2}
                  maxRows={4}
                  className="community-app-card-modal__review-notes-input"
                />
              </div>
              <div className="community-app-card-modal__review-decision-buttons">
                <RevoltButton
                  variant="primary"
                  size="sm"
                  onClick={handleUpdateReviewStatus}
                  disabled={isSavingStatus || isLoadingDetails}
                  aria-label="Update review status"
                >
                  {isSavingStatus ? (
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  ) : (
                    <i className="fa-solid fa-check" aria-hidden />
                  )}{' '}
                  Update status
                </RevoltButton>
              </div>
            </div>
          )}

          <div className="community-app-card-modal__review-controls">
            <div className="community-app-card-modal__control">
              <label htmlFor={`community-user-review-mode-${appId}`} className="community-app-card-modal__label">
                User review override
              </label>
              <DropdownSelect
                id={`community-user-review-mode-${appId}`}
                value={userReviewMode}
                options={REVIEW_MODE_OPTIONS}
                onChange={handleUserReviewModeChange}
                className="community-app-card-modal__select"
                menuClassName="app-management-sort-menu"
                disabled={!owner?.id || isSavingUserMode || isLoadingDetails}
              />
            </div>
            <div className="community-app-card-modal__control">
              <label htmlFor={`community-app-review-mode-${appId}`} className="community-app-card-modal__label">
                App review override
              </label>
              <DropdownSelect
                id={`community-app-review-mode-${appId}`}
                value={appReviewMode}
                options={REVIEW_MODE_OPTIONS}
                onChange={handleAppReviewModeChange}
                className="community-app-card-modal__select"
                menuClassName="app-management-sort-menu"
                disabled={!appId || isSavingAppMode || isLoadingDetails}
              />
            </div>
          </div>

          <div className="community-app-card-modal__review-note">
            User override affects all public apps for this owner. App override takes priority over the user override.
          </div>
          {currentReview && (
            <div className="community-app-card-modal__review-note">
              Current review: {formatReviewStatus(currentReview.status)} on {formatDate(currentReview.submittedAt || currentReview.reviewedAt)}
            </div>
          )}
          </div>
        </div>
      </div>
      <div className="community-app-card-modal__footer">
        <div className="community-app-card-modal__footer-left">
          <RevoltButton
            variant="ghost"
            size="sm"
            as="a"
            href={`/app-settings/${app.id}`}
            data-navigation-button
            onClick={(e) => {
              e.preventDefault();
              handleSettings();
            }}
            onContextMenu={(e) => e.stopPropagation()}
            aria-label="Open settings"
          >
            <i className="fa-solid fa-gear" aria-hidden />
          </RevoltButton>
        </div>
        <div className="community-app-card-modal__footer-right">
          <RevoltButton
            variant="ghost"
            size="sm"
            as="a"
            href={`/playspace/${app.id}?mode=sharing`}
            data-navigation-button
            onClick={(e) => {
              e.preventDefault();
              handlePlayspace();
            }}
            onContextMenu={(e) => e.stopPropagation()}
          >
            Playspace
          </RevoltButton>
          <RevoltButton
            variant="accent"
            size="sm"
            as="a"
            href={`/workspace/${app.id}`}
            data-navigation-button
            onClick={(e) => {
              e.preventDefault();
              handleWorkspace();
            }}
            onContextMenu={(e) => e.stopPropagation()}
          >
            Workspace
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}
